/*
 * AMASS Shop / Collection page controller (US-008)
 * ----------------------------------------------------------------------------
 * Two responsibilities, both layered on Horizon's native cart plumbing:
 *
 *   1. <amass-shop-tabs> — the POWDERS / CANS / BUNDLES pill toggle.
 *      - Reflects / restores the active tab from the ?tab= query param
 *        (?tab=cans | ?tab=powders | ?tab=bundles), defaulting to "cans".
 *      - Swaps which curated grid is visible (no network fetch — both grids are
 *        server-rendered from the curated collections and toggled client-side,
 *        so the swap is instant and SEO-safe).
 *      - Updates the URL with history.replaceState so the tab is shareable and
 *        survives reload/back without adding history noise.
 *
 *   2. <amass-quick-add> — the inline pack-size chips + per-card Add to Cart.
 *      - Reads the per-card variant map from <script data-amass-card-variants>
 *        (the same shape US-005 emits: [{size,id,price,available,unit,unitLabel}]).
 *      - Selecting a size chip updates the live variant + price + the
 *        "ADD - $XX.XX" button label, mirroring the PDP behaviour.
 *      - Add to Cart POSTs to Theme.routes.cart_add_url with the cart-drawer
 *        section ids appended, then dispatches the SAME cart:update (CartAddEvent)
 *        event Horizon's cart-items-component + the US-004 drawer/free-shipping
 *        bar already listen for, and opens the drawer. No bespoke cart state.
 *
 * SAVE 15% / 20% labels are DISPLAY-ONLY (references.md §2) — emitted in Liquid,
 * never recomputed here, and never tied to compare_at / discounts.
 */
(function () {
  'use strict';

  function routesRoot() {
    if (window.Theme && window.Theme.routes && window.Theme.routes.root_url) {
      var root = window.Theme.routes.root_url;
      return root === '/' ? '/' : root.replace(/\/+$/, '') + '/';
    }
    return '/';
  }

  function cartAddUrl() {
    if (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) {
      return window.Theme.routes.cart_add_url;
    }
    return routesRoot() + 'cart/add.js';
  }

  function formatMoney(cents) {
    var amount = (parseInt(cents, 10) || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD',
      }).format(amount);
    } catch (e) {
      return '$' + amount.toFixed(2);
    }
  }

  /* ===================================================================== */
  /* <amass-shop-tabs> — CANS / POWDERS toggle + ?tab= sync                 */
  /* ===================================================================== */
  var VALID_TABS = ['cans', 'powders', 'bundles'];

  function normalizeTab(value) {
    if (!value) return null;
    var v = String(value).toLowerCase().trim();
    return VALID_TABS.indexOf(v) !== -1 ? v : null;
  }

  class AmassShopTabs extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.buttons = Array.prototype.slice.call(this.querySelectorAll('[data-amass-tab-button]'));
      // Tabs this page actually has. "bundles" only exists where a Bundles
      // collection is chosen, so ?tab=bundles elsewhere falls back to the default.
      this.tabs = this.buttons.map(function (btn) {
        return btn.getAttribute('data-amass-tab-button');
      });

      // Grid panels live inside this element; the header description panels are
      // siblings in .amass-shop__header. Collect BOTH from the shared section
      // root so the toggle swaps the description in lockstep with the grid.
      var scope = this.closest('.amass-shop') || this;
      this.panels = Array.prototype.slice
        .call(scope.querySelectorAll('[data-amass-tab-panel]'))
        .filter(function (el, i, arr) {
          return arr.indexOf(el) === i;
        });

      var initial =
        this.known(normalizeTab(new URL(window.location.href).searchParams.get('tab'))) ||
        normalizeTab(this.getAttribute('data-default-tab')) ||
        'cans';

      this.buttons.forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.activate(btn.getAttribute('data-amass-tab-button'), true);
        });
      });

      // React to back/forward navigation between tabs.
      window.addEventListener('popstate', function () {
        var t =
          self.known(normalizeTab(new URL(window.location.href).searchParams.get('tab'))) || 'cans';
        self.activate(t, false);
      });

      this.activate(initial, false);
    }

    // A tab name is usable only when this page rendered a button for it (a page
    // with a single grid renders no buttons at all and keeps the old behaviour).
    known(tab) {
      if (!tab || !this.tabs.length) return tab;
      return this.tabs.indexOf(tab) !== -1 ? tab : null;
    }

    activate(tab, pushUrl) {
      tab = this.known(normalizeTab(tab)) || 'cans';

      this.buttons.forEach(function (btn) {
        var on = btn.getAttribute('data-amass-tab-button') === tab;
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        btn.classList.toggle('is-active', on);
      });

      this.panels.forEach(function (panel) {
        var on = panel.getAttribute('data-amass-tab-panel') === tab;
        panel.hidden = !on;
        panel.setAttribute('aria-hidden', on ? 'false' : 'true');
      });

      if (pushUrl) {
        var url = new URL(window.location.href);
        url.searchParams.set('tab', tab);
        try {
          window.history.replaceState({ amassTab: tab }, '', url.toString());
        } catch (e) {
          /* ignore (e.g. sandboxed preview) */
        }
      }

      this.dispatchEvent(
        new CustomEvent('amass:tab-change', { bubbles: true, detail: { tab: tab } })
      );
    }
  }

  if (!customElements.get('amass-shop-tabs')) {
    customElements.define('amass-shop-tabs', AmassShopTabs);
  }

  /* ===================================================================== */
  /* <amass-quick-add> — inline size chips + per-card Add to Cart           */
  /* ===================================================================== */
  class AmassQuickAdd extends HTMLElement {
    connectedCallback() {
      this.variants = this.#readJson('[data-amass-card-variants]', []);
      this.chips = Array.prototype.slice.call(this.querySelectorAll('[data-amass-size-chip]'));
      this.inputs = Array.prototype.slice.call(this.querySelectorAll('input[name^="amass-card-size-"]'));
      this.priceEl = this.querySelector('[data-amass-card-price]');
      this.unitEl = this.querySelector('[data-amass-card-unit]');
      this.atcButton = this.querySelector('[data-amass-card-atc]');
      this.atcLabelEl = this.querySelector('[data-amass-card-atc-label]');
      this.variantIdInput = this.querySelector('[data-amass-card-variant-id]');
      this.form = this.querySelector('form[data-amass-card-form]');

      this.#wireSizeSelection();
      this.#wireAddToCart();
      this.#syncFromSelection();
    }

    #readJson(selector, fallback) {
      var el = this.querySelector(selector);
      if (!el) return fallback;
      try {
        return JSON.parse(el.textContent);
      } catch (e) {
        return fallback;
      }
    }

    #currentVariant() {
      var checked = this.querySelector('input[name^="amass-card-size-"]:checked');
      var size = checked ? checked.value : null;
      var match = null;
      for (var i = 0; i < this.variants.length; i++) {
        if (this.variants[i].size === size) {
          match = this.variants[i];
          break;
        }
      }
      if (!match && this.variants.length) match = this.variants[0];
      return match;
    }

    #wireSizeSelection() {
      var self = this;
      this.inputs.forEach(function (input) {
        input.addEventListener('change', function () {
          self.#syncChipState();
          self.#syncFromSelection();
        });
      });
    }

    #syncChipState() {
      this.chips.forEach(function (chip) {
        var input = chip.querySelector('input[name^="amass-card-size-"]');
        var on = !!(input && input.checked);
        chip.classList.toggle('is-selected', on);
        chip.setAttribute('aria-checked', on ? 'true' : 'false');
      });
    }

    #syncFromSelection() {
      var variant = this.#currentVariant();
      if (!variant) return;

      if (this.variantIdInput) this.variantIdInput.value = variant.id;

      if (this.priceEl && variant.price != null) {
        this.priceEl.textContent = formatMoney(variant.price);
      }

      if (this.unitEl) {
        if (variant.unit && variant.unitLabel) {
          this.unitEl.textContent = formatMoney(variant.unit) + ' / ' + variant.unitLabel;
          this.unitEl.hidden = false;
        } else {
          this.unitEl.hidden = true;
        }
      }

      var unavailable = variant.available === false;
      if (this.atcButton) {
        this.atcButton.disabled = unavailable;
        this.atcButton.setAttribute('aria-disabled', unavailable ? 'true' : 'false');
      }
      if (this.atcLabelEl) {
        if (unavailable) {
          this.atcLabelEl.textContent = 'Unavailable';
        } else {
          this.atcLabelEl.textContent = 'Add – ' + formatMoney(variant.price);
        }
      }
    }

    #wireAddToCart() {
      var self = this;
      if (!this.form) return;
      this.form.addEventListener('submit', function (e) {
        e.preventDefault();
        self.#addToCart();
      });
    }

    #addToCart() {
      var self = this;
      var variant = this.#currentVariant();
      if (!variant || variant.available === false) return;
      if (this.atcButton && this.atcButton.disabled) return;

      var formData = new FormData();
      formData.append('id', variant.id);
      formData.append('quantity', 1);

      // Append the cart-drawer section ids so Horizon morphs them in place.
      var sectionIds = [];
      document.querySelectorAll('cart-items-component, cart-drawer-component').forEach(function (el) {
        if (el.dataset && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
      });
      if (sectionIds.length) formData.append('sections', sectionIds.join(','));

      if (this.atcButton) this.atcButton.classList.add('is-loading');

      fetch(cartAddUrl(), {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      })
        .then(function (r) { return r.json(); })
        .then(function (item) {
          if (item && item.status) {
            self.#dispatchCartError(item);
            return;
          }
          self.#refreshCart();
        })
        .catch(function () { /* leave UI usable */ })
        .finally(function () {
          if (self.atcButton) self.atcButton.classList.remove('is-loading');
        });
    }

    #refreshCart() {
      var self = this;
      fetch(routesRoot() + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          document.dispatchEvent(
            new CustomEvent('cart:update', {
              bubbles: true,
              detail: {
                resource: cart,
                sourceId: self.id || 'amass-quick-add',
                data: { source: 'amass-quick-add', itemCount: cart.item_count },
              },
            })
          );
          self.#openCartDrawer();
        })
        .catch(function () {});
    }

    #openCartDrawer() {
      var drawer = document.querySelector('cart-drawer-component');
      if (drawer) {
        if (typeof drawer.open === 'function') { drawer.open(); return; }
        if (typeof drawer.showDialog === 'function') { drawer.showDialog(); return; }
        var dialog = drawer.querySelector('dialog');
        if (dialog && typeof dialog.showModal === 'function') { dialog.showModal(); return; }
      }
      var toggle = document.querySelector('[data-cart-drawer-toggle], a[href$="/cart"], .header-actions__cart-icon');
      if (toggle && toggle.click) toggle.click();
    }

    #dispatchCartError(payload) {
      document.dispatchEvent(
        new CustomEvent('cart:error', {
          bubbles: true,
          detail: { data: { message: (payload && payload.description) || (payload && payload.message) || '' } },
        })
      );
    }
  }

  if (!customElements.get('amass-quick-add')) {
    customElements.define('amass-quick-add', AmassQuickAdd);
  }
})();
